/**
 * NEON DISTRICT — realistic cyberpunk-street pass (2026-11-04, owner: "improve
 * each map", "realistic").
 *
 * The map is three thin-walled towers inside a 14 m perimeter wall, under a
 * night sky. Before this pass the perimeter was a blank 70 m slab on every
 * side, the towers had no windows (their walls carry a `glow` accent, which the
 * house-window pass skips), the ground was a dry grid and the world ended at
 * the wall top. Inspiration (not copies): night street photography of Tokyo
 * back streets / Hong Kong tenements, and the usual cyberpunk street kits —
 * stacked shop frontages, roller shutters, lit tenement windows, wall-pack
 * lamps, slim AC units, vertical tile signs, string lights and cable bundles
 * strung over the alleys, wet puddles, steam from street vents, and a lit
 * skyline beyond the block.
 *
 * Structure: `neonDistrictLayout` is PURE (no three.js, no DOM) and returns a
 * flat list of pieces, so `agent/bench-extras-neon_district.mjs` checks the
 * collision contract against the real shared boxes. `build` turns the list
 * into ONE merged mesh per material — 7 draw calls total, built once, no
 * per-frame work, no lights (emissive + the neon bloom does the glow).
 *
 * Contract (see ../mapExtras.ts):
 *  - wall-mounted pieces sit ON a collider face, ≤ WALL_PROUD (0.26 m) proud;
 *  - ground pieces are decals ≤ GROUND_LIFT high (puddles, grates);
 *  - cables hang between two collider faces, never through any box, with head
 *    room over every walkable surface below them;
 *  - the skyline is entirely OUTSIDE the map bounds (unreachable).
 */
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { MapBox, MapDef } from "@neon-purge/shared";
import type { MapExtrasContext } from "../mapExtras.js";
import { mapTextures, surfaceMaps, worldProjectUV } from "../mapTextures.js";

export const mapIds = ["neon_district"];

// ---------------------------------------------------------------------------
// contract numbers (the bench quotes these)
export const WALL_PROUD = 0.26;
export const GROUND_LIFT = 0.05;
export const CABLE_HEADROOM = 2.4;
export const SKYLINE_MARGIN = 2;

export type ExtrasMat = "metal" | "neon" | "ads" | "win" | "puddle" | "steam" | "sky";
export type Zone = "wall" | "ground" | "cable" | "skyline" | "fx";

/** One piece. `shape` box = axis-aligned box; quad = single face with normal
 *  (nx,ny,nz) at the box's outer side; seg = thin box from a to b. */
export interface ExtrasPiece {
  mat: ExtrasMat;
  zone: Zone;
  shape: "box" | "quad" | "seg";
  /** centre + full size (for quads: the plane's centre, one size is 0) */
  x: number; y: number; z: number;
  sx: number; sy: number; sz: number;
  nx: number; ny: number; nz: number;
  /** segment endpoints (seg only) */
  a?: [number, number, number]; b?: [number, number, number];
  color: number;
  /** atlas cell 0..3 (ads / win quads) */
  cell: number;
  /** host collider index (wall + ground pieces) */
  host: number;
}

// deterministic hash in [0,1)
function h01(a: number, b: number, salt: number): number {
  let x = Math.imul((a * 73856093) ^ (b * 19349663) ^ (salt * 83492791), 0x9e3779b1);
  x ^= x >>> 15; x = Math.imul(x, 0x85ebca6b); x ^= x >>> 13;
  return ((x >>> 0) % 100000) / 100000;
}

interface AABB { x0: number; y0: number; z0: number; x1: number; y1: number; z1: number }
const boxAABB = (b: MapBox): AABB => ({
  x0: b.p[0] - b.s[0] / 2, x1: b.p[0] + b.s[0] / 2,
  y0: b.p[1] - b.s[1] / 2, y1: b.p[1] + b.s[1] / 2,
  z0: b.p[2] - b.s[2] / 2, z1: b.p[2] + b.s[2] / 2,
});
const overlaps = (a: AABB, b: AABB, e = 0): boolean =>
  a.x0 < b.x1 - e && a.x1 > b.x0 + e && a.y0 < b.y1 - e && a.y1 > b.y0 + e && a.z0 < b.z1 - e && a.z1 > b.z0 + e;

/** One wall face we hang things on. Tangent axis is the other horizontal one. */
interface Face {
  host: number;
  axis: "x" | "z";      // the face's NORMAL axis
  plane: number;        // world coordinate of the face plane
  sign: 1 | -1;         // outward normal sign along `axis`
  t0: number; t1: number; // tangent extent
  y0: number; y1: number; // vertical extent
  /** (t0, t1, y0, y1) rectangles other passes already occupy */
  keep?: [number, number, number, number][];
}

const PALETTE = {
  concrete: 0x2a2e38, darkMetal: 0x1a1d24, steel: 0x6c7380, acWhite: 0xa9aeb6,
  shutter: 0x50555e, glassDark: 0x0b0f16, cable: 0x0c0d10, rust: 0x5a4a3e,
  pilaster: 0x343846, sky: [0x23293a, 0x1d2231, 0x2a2f3d, 0x1a1e2a, 0x2e2a36],
};
const NEON = [0xff2ec4, 0x00f0ff, 0xffb636, 0x9dff00, 0xff4060, 0x8a5cff];
const WARM_BULB = 0xffc070;
/** scale a hex colour (the neon mesh multiplies by 2.2 for bloom; a lit
 *  apartment window must stay BELOW the bloom threshold or the facade fizzes) */
const dim = (hex: number, f: number): number =>
  (Math.round(((hex >> 16) & 255) * f) << 16) | (Math.round(((hex >> 8) & 255) * f) << 8) | Math.round((hex & 255) * f);
/** lit apartment pane colours: warm tungsten, cool LED, TV blue, neon spill */
const PANE = [0xffcf8a, 0xffe2b8, 0xa8d4ff, 0x7f9cff, 0xff9ad8];

/** Everything, as data. Pure — no three.js objects are created here. */
export function neonDistrictLayout(map: MapDef, lowSpec: boolean): ExtrasPiece[] {
  const boxes = map.boxes;
  const aabbs = boxes.map(boxAABB);
  const out: ExtrasPiece[] = [];
  // the ground slab: the widest flat box at y≈0
  let ground = 0;
  for (let i = 0; i < boxes.length; i++) if (boxes[i].s[0] * boxes[i].s[2] > boxes[ground].s[0] * boxes[ground].s[2]) ground = i;
  const groundTop = aabbs[ground].y1;

  /** is this region free of every collider except `skip` + the ground? */
  const clear = (r: AABB, skip: number): boolean => {
    for (let i = 0; i < aabbs.length; i++) {
      if (i === skip || i === ground) continue;
      if (overlaps(r, aabbs[i], 0.005)) return false;
    }
    return true;
  };

  const push = (p: Partial<ExtrasPiece> & Pick<ExtrasPiece, "mat" | "zone" | "shape" | "x" | "y" | "z" | "sx" | "sy" | "sz">): void => {
    out.push({ nx: 0, ny: 0, nz: 0, color: 0xffffff, cell: 0, host: -1, ...p });
  };

  /** Place a box on a face: tangent centre t, vertical centre y, tangent width
   *  w, height hh, projection `proud` (measured from the face plane). The box is
   *  embedded 1 cm into the wall so it never shows a seam. Returns false (and
   *  places nothing) when anything else is in the way. */
  const onFace = (f: Face, mat: ExtrasMat, t: number, y: number, w: number, hh: number, proud: number,
    color: number, opts: { quad?: boolean; cell?: number; check?: number } = {}): boolean => {
    if (t - w / 2 < f.t0 - 1e-6 || t + w / 2 > f.t1 + 1e-6 || y - hh / 2 < f.y0 - 1e-6 || y + hh / 2 > f.y1 + 1e-6) return false;
    if (f.keep) for (const k of f.keep) if (t + w / 2 > k[0] && t - w / 2 < k[1] && y + hh / 2 > k[2] && y - hh / 2 < k[3]) return false;
    proud = Math.min(proud, WALL_PROUD);
    const nC = f.plane + f.sign * (proud / 2 - 0.005);
    const nS = proud + 0.01;
    const cx = f.axis === "x" ? nC : t, cz = f.axis === "z" ? nC : t;
    const sx = f.axis === "x" ? nS : w, sz = f.axis === "z" ? nS : w;
    // clearance: the piece itself plus `check` metres in front of it
    const ext = opts.check ?? 0.05;
    const r: AABB = {
      x0: cx - sx / 2, x1: cx + sx / 2, y0: y - hh / 2, y1: y + hh / 2, z0: cz - sz / 2, z1: cz + sz / 2,
    };
    if (f.axis === "x") { if (f.sign > 0) r.x1 += ext; else r.x0 -= ext; }
    else { if (f.sign > 0) r.z1 += ext; else r.z0 -= ext; }
    if (!clear(r, f.host)) return false;
    if (opts.quad) {
      const q = f.plane + f.sign * proud;
      push({
        mat, zone: "wall", shape: "quad", host: f.host, color, cell: opts.cell ?? 0,
        x: f.axis === "x" ? q : t, y, z: f.axis === "z" ? q : t,
        sx: f.axis === "x" ? 0 : w, sy: hh, sz: f.axis === "z" ? 0 : w,
        nx: f.axis === "x" ? f.sign : 0, nz: f.axis === "z" ? f.sign : 0,
      });
    } else {
      push({ mat, zone: "wall", shape: "box", host: f.host, color, x: cx, y, z: cz, sx, sy: hh, sz });
    }
    return true;
  };

  /** A window pane on its frame: lit (a dim glowing pane with a pulled-down
   *  blind of random depth, the way a tenement reads at night) with
   *  probability `pLit`, dark glazing otherwise. */
  const litPane = (f: Face, t: number, y: number, w: number, hh: number, rr: number, pLit: number): void => {
    if (rr >= pLit) { onFace(f, "metal", t, y, w, hh, 0.07, PALETTE.glassDark); return; }
    const c = PANE[Math.floor(rr * 97) % PANE.length];
    const blind = 0.15 + ((rr * 7.31) % 1) * 0.45;            // fraction covered from the top
    const bh = hh * blind;
    onFace(f, "neon", t, y - bh / 2, w, hh - bh, 0.068, dim(c, 0.24 + ((rr * 3.7) % 1) * 0.12));
    onFace(f, "neon", t, y + hh / 2 - bh / 2, w, bh, 0.068, dim(c, 0.09));
    if (!lowSpec && ((rr * 13.3) % 1) < 0.5) onFace(f, "metal", t, y, 0.05, hh, 0.075, PALETTE.darkMetal); // mullion
  };

  // ---- classify the structure -------------------------------------------
  const perim: Face[] = [];
  const towerWalls: Face[] = [];
  const roofs = boxes.filter((b) => Math.abs(b.s[0] - 12) < 0.01 && Math.abs(b.s[2] - 12) < 0.01 && b.s[1] < 0.5);
  for (let i = 0; i < boxes.length; i++) {
    const b = boxes[i]; const a = aabbs[i];
    const thinX = b.s[0] <= 1.2 && b.s[2] > 4, thinZ = b.s[2] <= 1.2 && b.s[0] > 4;
    if (!thinX && !thinZ) continue;
    const axis: "x" | "z" = thinX ? "x" : "z";
    const c = axis === "x" ? b.p[0] : b.p[2];
    if (b.s[1] >= 12 && Math.max(b.s[0], b.s[2]) >= 60) {
      // perimeter: the face that points at the map centre
      const sign: 1 | -1 = c > 0 ? -1 : 1;
      const plane = c + sign * (axis === "x" ? b.s[0] : b.s[2]) / 2;
      const lim = 34 - 0.01; // stop at the neighbouring wall's inner face
      perim.push({ host: i, axis, plane, sign, t0: -lim, t1: lim, y0: groundTop, y1: a.y1 });
    } else if (b.s[1] >= 8 && b.s[1] <= 10 && a.y0 <= groundTop + 0.01) {
      // a tower wall (full height): outward = away from its tower's roof centre
      let best: MapBox | null = null, bd = Infinity;
      for (const r of roofs) { const d = Math.hypot(r.p[0] - b.p[0], r.p[2] - b.p[2]); if (d < bd) { bd = d; best = r; } }
      if (!best || bd > 9) continue;
      const tc = axis === "x" ? best.p[0] : best.p[2];
      const sign: 1 | -1 = c > tc ? 1 : -1;
      const plane = c + sign * (axis === "x" ? b.s[0] : b.s[2]) / 2;
      const tc0 = axis === "x" ? a.z0 : a.x0, tc1 = axis === "x" ? a.z1 : a.x1;
      towerWalls.push({ host: i, axis, plane, sign, t0: tc0, t1: tc1, y0: groundTop, y1: a.y1 });
    }
  }

  // ---- 1. PERIMETER FRONTAGES ------------------------------------------
  // The renderer already dresses each 70 m wall (mapDressing.dressStructure)
  // with 4 mullion fins at t = −21/−7/7/21, belt courses at y 4 and 9 and a
  // cornice; decor.ts hangs cloth banners at t ≈ −17/0/17 (y 4.9–8.5); the
  // signage pass puts a shopfront at t = 0 and a 1 m blade at |t| ≈ 22.4
  // (y 7.4–11.6). This pass FILLS the frame those give: each fin bay becomes
  // two tenement frontages — a shop storey (roller shutter or lit window,
  // fascia sign, wall-pack lamp), three rows of windows with slim AC units, a
  // vertical tile sign or a top billboard on some — and keeps clear of every
  // one of the existing pieces via keep-out rectangles.
  for (let wi = 0; wi < perim.length; wi++) {
    const f = perim[wi];
    const hb = boxes[f.host];
    const len = Math.max(hb.s[0], hb.s[2]);
    const nFin = Math.min(4, Math.floor(len / 3.2) - 1);
    const fins: number[] = [];
    for (let i = 1; i <= nFin; i++) fins.push((i / (nFin + 1) - 0.5) * len);
    const base = f.y0;
    // keep-outs in (t, y): fins, banners, the blade, the central shopfront
    const keep: [number, number, number, number][] = [];
    for (const t of fins) keep.push([t - 0.32, t + 0.32, base, base + 11.2]);
    for (const t of [-17, 0, 17]) keep.push([t - 1.5, t + 1.5, base + 4.7, base + 8.7]);
    for (const t of [-22.4, 22.4]) keep.push([t - 0.6, t + 0.6, base + 7.2, base + 11.8]);
    keep.push([-4.4, 4.4, base, base + 4.4]);
    // belt courses (renderer) every 5 m up from the wall's own base
    const belts: [number, number, number, number][] = [];
    for (let by = aabbs[f.host].y0 + 5; by < aabbs[f.host].y1 - 1.5; by += 5) belts.push([f.t0, f.t1, by - 0.14, by + 0.14]);
    f.keep = [...keep, ...belts];
    const bounds = [f.t0, ...fins.flatMap((t) => [t - 0.32, t + 0.32]), f.t1];
    // sub-bays: each fin bay split in two at a slim party-wall pilaster
    const bays: [number, number][] = [];
    for (let k = 0; k + 1 < bounds.length; k += 2) {
      const b0 = bounds[k], b1 = bounds[k + 1], mid = (b0 + b1) / 2 + (h01(wi, k, 2) - 0.5) * 2;
      bays.push([b0, mid - 0.15], [mid + 0.15, b1]);
      f.keep = keep; // the pilaster runs THROUGH the belts like the fins do
      onFace(f, "metal", mid, base + 5.6, 0.3, 11.0, 0.12, PALETTE.pilaster);
      f.keep = [...keep, ...belts];
    }
    for (let bi = 0; bi < bays.length; bi++) {
      const [b0, b1] = bays[bi];
      const bw = b1 - b0, bc = (b0 + b1) / 2;
      const r = (s: number): number => h01(wi * 31 + bi, bi, s);
      // ---- shop storey (0..4) ----
      const shopW = Math.min(bw - 2.0, 4.6);
      if (shopW >= 2.4) {
        let shop = false;
        if (r(2) < 0.45) {
          // ROLLER SHUTTER: housing box + ribbed curtain
          if (onFace(f, "metal", bc, base + 2.95, shopW + 0.2, 0.32, 0.22, PALETTE.darkMetal)) {
            shop = true;
            onFace(f, "metal", bc, base + 1.4, shopW, 2.78, 0.05, PALETTE.shutter);
            if (!lowSpec) for (let k = 0; k < 13; k++) onFace(f, "metal", bc, base + 0.12 + k * 0.21, shopW, 0.05, 0.08, 0x3d4149);
          }
        } else if (onFace(f, "metal", bc, base + 1.75, shopW + 0.2, 2.5, 0.06, PALETTE.darkMetal)) {
          // LIT SHOP WINDOW: frame, glazing (lit-room atlas), stall riser
          shop = true;
          onFace(f, "win", bc, base + 1.9, shopW - 0.1, 2.0, 0.07, 0xffffff, { quad: true, cell: Math.floor(r(3) * 4) });
          onFace(f, "metal", bc, base + 0.4, shopW + 0.2, 0.8, 0.09, PALETTE.concrete);
          onFace(f, "neon", bc, base + 3.07, shopW, 0.06, 0.1, NEON[Math.floor(r(4) * NEON.length)]);
        }
        if (shop) {
          // fascia sign: two square atlas tiles side by side so glyphs stay square
          const fh = 0.62, ftw = fh * 1.15;
          if (onFace(f, "metal", bc, base + 3.5, ftw * 2 + 0.16, fh + 0.14, 0.16, PALETTE.darkMetal)) {
            onFace(f, "ads", bc - ftw / 2, base + 3.5, ftw, fh, 0.165, 0xffffff, { quad: true, cell: Math.floor(r(5) * 4) });
            onFace(f, "ads", bc + ftw / 2, base + 3.5, ftw, fh, 0.165, 0xffffff, { quad: true, cell: Math.floor(r(6) * 4) });
          }
          // wall-pack lamp to the left, meter box + conduit to the right
          const lt = bc - shopW / 2 - 0.45;
          if (onFace(f, "metal", lt, base + 2.7, 0.34, 0.26, 0.2, PALETTE.darkMetal)) onFace(f, "neon", lt, base + 2.56, 0.26, 0.03, 0.21, WARM_BULB);
          onFace(f, "metal", bc + shopW / 2 + 0.5, base + 1.5, 0.45, 0.6, 0.15, 0x5b6068);
          onFace(f, "metal", bc + shopW / 2 + 0.5, base + 2.6, 0.08, 1.6, 0.1, PALETTE.steel);
        }
      }
      // ---- upper storeys ----
      // vertical tile sign (stands 0.2 proud so it rides over the belt course)
      const colT = bc + (r(9) - 0.5) * Math.max(0, bw - 2.4);
      let hasColumn = false;
      if (bw >= 4 && r(8) < 0.5) {
        const tw = 1.25, n = lowSpec ? 3 : 4, y0 = base + 5.4, top = y0 + n * tw;
        const cy = (y0 + top) / 2, ch = n * tw + 0.2;
        f.keep = keep; // belts may pass behind it
        if (onFace(f, "metal", colT, cy, tw + 0.2, ch, 0.2, PALETTE.darkMetal)) {
          hasColumn = true;
          for (let k = 0; k < n; k++) onFace(f, "ads", colT, y0 + 0.1 + tw / 2 + k * tw, tw - 0.08, tw - 0.08, 0.205, 0xffffff, { quad: true, cell: Math.floor(r(10 + k) * 4) });
          const nc = NEON[Math.floor(r(15) * NEON.length)];
          onFace(f, "neon", colT - tw / 2 - 0.13, cy, 0.05, ch, 0.22, nc);
          onFace(f, "neon", colT + tw / 2 + 0.13, cy, 0.05, ch, 0.22, nc);
        }
        f.keep = [...keep, ...belts];
      }
      // top billboard on some bays without a column (above the y=9 belt)
      let topBoard = false;
      if (!hasColumn && bw >= 4.5 && r(16) < 0.45) {
        const bh = 1.9, bwid = Math.min(bw - 0.8, 5.6), cy = base + 10.55;
        if (onFace(f, "metal", bc, cy, bwid + 0.2, bh + 0.2, 0.12, PALETTE.darkMetal)) {
          topBoard = true;
          const n = Math.max(1, Math.round(bwid / bh));
          const tw = bwid / n;
          for (let k = 0; k < n; k++) onFace(f, "ads", bc - bwid / 2 + tw * (k + 0.5), cy, tw - 0.05, bh - 0.05, 0.125, 0xffffff, { quad: true, cell: Math.floor(r(20 + k) * 4) });
          onFace(f, "neon", bc, cy - bh / 2 - 0.14, bwid + 0.1, 0.05, 0.15, NEON[Math.floor(r(17) * NEON.length)]);
        }
      }
      // windows: three rows between the belts
      const nWin = Math.max(1, Math.floor((bw - 0.4) / 1.9));
      const pitch = bw / nWin;
      const rows = [base + 5.4, base + 7.75, base + 10.55];
      for (let s = 0; s < 3; s++) {
        if (topBoard && s === 2) continue;
        const wy = rows[s];
        for (let k = 0; k < nWin; k++) {
          const wt = b0 + pitch * (k + 0.5);
          if (hasColumn && Math.abs(wt - colT) < 1.6) continue;
          const rr = h01(wi * 97 + bi * 13 + k, s, 30);
          if (!onFace(f, "metal", wt, wy, 1.2, 1.5, 0.06, PALETTE.darkMetal)) continue;
          litPane(f, wt, wy + 0.02, 1.04, 1.32, rr, 0.5);
          onFace(f, "metal", wt, wy - 0.8, 1.34, 0.08, 0.12, PALETTE.concrete);
          // slim split-AC unit under ~1 in 3 upper windows
          if (s > 0 && h01(wi, bi * 7 + k, 40 + s) < 0.35) {
            const at = wt + (h01(k, s, 41) - 0.5) * 0.3;
            if (onFace(f, "metal", at, wy - 1.13, 0.86, 0.52, 0.24, PALETTE.acWhite)) {
              onFace(f, "metal", at + 0.12, wy - 1.13, 0.46, 0.4, 0.25, 0x6d727a);
            }
          }
        }
      }
    }
  }

  // ---- 2. TOWERS --------------------------------------------------------
  // Each 12 m tower wall already carries 2 fins (t = ±2), a belt at y 5, a
  // ground-floor shopfront and a fascia at the middle, and a blade sign at
  // t ≈ ±3.84 (y 5.4–8.1). Windows go in the slots that leaves: outer columns
  // at ±4.95 on both upper rows, the centre column on the top row.
  for (let fi = 0; fi < towerWalls.length; fi++) {
    const f = towerWalls[fi];
    const len = f.t1 - f.t0, tc = (f.t0 + f.t1) / 2;
    if (len < 10) continue; // the split front walls are signage territory
    const slots: [number, number, number][] = [[-4.95, 4.0, 1.2], [4.95, 4.0, 1.2], [-4.95, 6.9, 1.2], [4.95, 6.9, 1.2], [0, 6.9, 1.8]];
    for (let k = 0; k < slots.length; k++) {
      const [dt, wy, ww] = slots[k];
      const wt = tc + dt;
      const rr = h01(fi, k, 50);
      if (!onFace(f, "metal", wt, f.y0 + wy, ww + 0.16, 1.62, 0.06, PALETTE.darkMetal, { check: 0.5 })) continue;
      litPane(f, wt, f.y0 + wy, ww, 1.46, rr, 0.6);
      if (wy > 5 && h01(fi, k, 51) < 0.4) onFace(f, "metal", wt, f.y0 + wy - 1.15, 0.8, 0.46, 0.24, PALETTE.acWhite, { check: 0.5 });
    }
    // neon tube down each end of the wall (reads as the tower's corner light)
    const col = NEON[fi % 3 === 0 ? 1 : fi % 3 === 1 ? 0 : 3];
    for (const e of [f.t0 + 0.24, f.t1 - 0.24]) {
      onFace(f, "neon", e, (f.y0 + f.y1) / 2, 0.07, f.y1 - f.y0 - 0.6, 0.1, col, { check: 0.3 });
    }
  }

  // ---- 3. CATWALK UNDERLIGHTS -------------------------------------------
  for (let i = 0; i < boxes.length; i++) {
    const b = boxes[i], a = aabbs[i];
    if (b.s[1] > 0.5 || a.y0 < 3 || Math.max(b.s[0], b.s[2]) < 15 || Math.min(b.s[0], b.s[2]) > 4) continue;
    const alongX = b.s[0] > b.s[2];
    const L = alongX ? b.s[0] : b.s[2];
    const W = alongX ? b.s[2] : b.s[0];
    const col = NEON[i % 2 === 0 ? 1 : 0];
    /** a strip under the deck at lateral offset `off`, split into the runs
     *  that are clear of every other collider (stairs, tower walls) */
    const strip = (mat: ExtrasMat, off: number, yc: number, th: number, wd: number, color: number): void => {
      const lo = (alongX ? a.x0 : a.z0) + 0.2, hi = (alongX ? a.x1 : a.z1) - 0.2;
      const STEP = 0.5;
      let runStart = NaN;
      const flush = (end: number): void => {
        if (!Number.isFinite(runStart) || end - runStart < 0.6) { runStart = NaN; return; }
        const c = (runStart + end) / 2, L2 = end - runStart;
        push({
          mat, zone: "wall", shape: "box", host: i, color,
          x: alongX ? c : b.p[0] + off, y: yc, z: alongX ? b.p[2] + off : c,
          sx: alongX ? L2 : wd, sy: th, sz: alongX ? wd : L2,
        });
        runStart = NaN;
      };
      for (let u = lo; u < hi - 1e-6; u += STEP) {
        const u1 = Math.min(hi, u + STEP);
        const r: AABB = alongX
          ? { x0: u, x1: u1, y0: yc - th / 2, y1: yc + th / 2, z0: b.p[2] + off - wd / 2, z1: b.p[2] + off + wd / 2 }
          : { x0: b.p[0] + off - wd / 2, x1: b.p[0] + off + wd / 2, y0: yc - th / 2, y1: yc + th / 2, z0: u, z1: u1 };
        if (clear(r, i)) { if (!Number.isFinite(runStart)) runStart = u; }
        else flush(u);
      }
      flush(hi);
    };
    for (const e of [-1, 1]) strip("neon", e * (W / 2 - 0.08), a.y0 - 0.025, 0.04, 0.05, col);
    // conduit pair run under the deck (inside the footprint, hugging it)
    for (const e of [-0.45, 0.45]) strip("metal", e, a.y0 - 0.07, 0.12, 0.12, PALETTE.steel);
  }

  // ---- 4. COVER CUBES → street furniture (vending machines / utility box) --
  for (let i = 0; i < boxes.length; i++) {
    const b = boxes[i], a = aabbs[i];
    if (Math.abs(b.s[1] - 2) > 0.01 || a.y0 > groundTop + 0.01 || Math.max(b.s[0], b.s[2]) > 3.5) continue;
    const faces: Face[] = [
      { host: i, axis: "x", plane: a.x1, sign: 1, t0: a.z0, t1: a.z1, y0: a.y0, y1: a.y1 },
      { host: i, axis: "x", plane: a.x0, sign: -1, t0: a.z0, t1: a.z1, y0: a.y0, y1: a.y1 },
      { host: i, axis: "z", plane: a.z1, sign: 1, t0: a.x0, t1: a.x1, y0: a.y0, y1: a.y1 },
      { host: i, axis: "z", plane: a.z0, sign: -1, t0: a.x0, t1: a.x1, y0: a.y0, y1: a.y1 },
    ];
    for (let k = 0; k < 4; k++) {
      const f = faces[k];
      const tc = (f.t0 + f.t1) / 2, len = f.t1 - f.t0;
      const vend = (i + k) % 2 === 0;
      if (vend) {
        // two vending machines side by side: lit front, dark selection panel
        for (const s of [-1, 1]) {
          const vt = tc + s * len / 4;
          const nc = NEON[(i + k + (s > 0 ? 1 : 0)) % NEON.length];
          onFace(f, "metal", vt, 1.0, len / 2 - 0.1, 1.94, 0.04, 0x30343c);
          onFace(f, "neon", vt - 0.12, 1.25, len / 2 - 0.62, 1.05, 0.06, dim(nc, 0.42));
          onFace(f, "metal", vt + (len / 4 - 0.28), 1.2, 0.3, 0.7, 0.07, 0x111318);
          onFace(f, "metal", vt, 0.3, len / 2 - 0.4, 0.22, 0.08, 0x0d0f13);
        }
      } else {
        // utility cabinet: louvres + a hazard-ish warm status lamp
        onFace(f, "metal", tc, 1.0, len - 0.3, 1.8, 0.04, 0x3a4048);
        for (let l = 0; l < 6; l++) onFace(f, "metal", tc, 0.55 + l * 0.14, len * 0.5, 0.04, 0.07, 0x23272e);
        onFace(f, "neon", tc + len * 0.32, 1.7, 0.1, 0.1, 0.06, 0xff4020);
      }
    }
    // a metal capping rim on top (inside the footprint, flush with the top)
    push({ mat: "metal", zone: "wall", shape: "box", host: i, color: 0x2b2f37, x: b.p[0], y: a.y1 - 0.04, z: b.p[2], sx: b.s[0] + 0.06, sy: 0.08, sz: b.s[2] + 0.06 });
  }

  // ---- 5. GROUND: puddles, street vents + steam ---------------------------
  const groundFree = (x: number, z: number, rx: number, rz: number): boolean =>
    clear({ x0: x - rx, x1: x + rx, y0: groundTop - 0.1, y1: groundTop + 2.5, z0: z - rz, z1: z + rz }, -1);
  const lim = 33;
  let puddles = 0;
  const maxP = lowSpec ? 10 : 22;
  for (let k = 0; k < 400 && puddles < maxP; k++) {
    const x = (h01(k, 1, 60) * 2 - 1) * lim, z = (h01(k, 2, 61) * 2 - 1) * lim;
    const rx = 0.9 + h01(k, 3, 62) * 2.2, rz = 0.7 + h01(k, 4, 63) * 1.6;
    if (Math.abs(x) + rx > lim || Math.abs(z) + rz > lim) continue;
    if (!groundFree(x, z, rx, rz)) continue;
    push({ mat: "puddle", zone: "ground", shape: "quad", host: ground, color: 0xffffff,
      x, y: groundTop + 0.012 + (puddles % 4) * 0.002, z, sx: rx * 2, sy: 0, sz: rz * 2, ny: 1, cell: puddles % 4 });
    puddles++;
  }
  const vents: [number, number][] = [[-27, -12], [24, -4], [-6, 29], [14, 28], [-29, 22], [27, -21]];
  for (const [vx, vz] of vents) {
    if (!groundFree(vx, vz, 0.8, 0.8)) continue;
    push({ mat: "metal", zone: "ground", shape: "box", host: ground, color: 0x2a2d33, x: vx, y: groundTop + 0.015, z: vz, sx: 1.2, sy: 0.03, sz: 0.8 });
    for (let s = 0; s < 6; s++) push({ mat: "metal", zone: "ground", shape: "box", host: ground, color: 0x0a0b0d, x: vx - 0.45 + s * 0.18, y: groundTop + 0.032, z: vz, sx: 0.07, sy: 0.006, sz: 0.62 });
    if (lowSpec) continue;
    // steam: crossed soft cards rising from the grate (non-solid fx)
    for (const rot of [0, 1]) push({ mat: "steam", zone: "fx", shape: "quad", host: ground, color: 0xffffff,
      x: vx, y: groundTop + 1.15, z: vz, sx: rot ? 0 : 1.7, sy: 2.3, sz: rot ? 1.7 : 0, nx: rot ? 1 : 0, nz: rot ? 0 : 1 });
  }

  // ---- 6. CABLES + STRING LIGHTS ----------------------------------------
  // Each run is anchored on two collider faces. Sampled for clearance: no box
  // may be within 0.15 m of the wire, and nothing walkable may be within
  // CABLE_HEADROOM below it.
  const tryCable = (a: [number, number, number], b: [number, number, number], sag: number, lights: boolean, seed: number): void => {
    const N = 14;
    const pts: [number, number, number][] = [];
    for (let i = 0; i <= N; i++) {
      const u = i / N;
      pts.push([a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u - sag * 4 * u * (1 - u), a[2] + (b[2] - a[2]) * u]);
    }
    for (let i = 1; i < N; i++) {
      const [x, y, z] = pts[i];
      if (y < groundTop + 4.5) return;
      if (!clear({ x0: x - 0.15, x1: x + 0.15, y0: y - CABLE_HEADROOM, y1: y + 0.15, z0: z - 0.15, z1: z + 0.15 }, -1)) return;
    }
    for (let i = 0; i < N; i++) {
      push({ mat: "metal", zone: "cable", shape: "seg", host: -1, color: PALETTE.cable,
        x: 0, y: 0, z: 0, sx: 0.035, sy: 0.035, sz: 0, a: pts[i], b: pts[i + 1] });
    }
    if (!lights) return;
    for (let i = 1; i < N; i++) {
      const [x, y, z] = pts[i];
      const c = (i + seed) % 3 === 0 ? 0xff2ec4 : (i + seed) % 3 === 1 ? WARM_BULB : 0x00f0ff;
      push({ mat: "neon", zone: "cable", shape: "box", host: -1, color: c, x, y: y - 0.08, z, sx: 0.1, sy: 0.13, sz: 0.1 });
    }
  };
  // across each corner of the perimeter (wall face → wall face)
  for (const [sx, sz] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
    for (const [d, y, sag, li] of [[8, 11.2, 1.1, true], [11, 12.0, 0.8, false], [13.5, 10.4, 1.4, true]] as const) {
      const fx = sx * 34 - sx * 0.02, fz = sz * 34 - sz * 0.02;
      tryCable([fx, y, sz * (34 - d)], [sx * (34 - d), y, fz], sag, li, d);
    }
  }
  // tower rear/outer walls → the nearest perimeter wall
  for (const f of towerWalls) {
    const len = f.t1 - f.t0;
    const target0 = f.sign * 34;
    if (Math.abs(target0 - f.plane) > 16) continue; // only across an alley
    // the split front walls carry the renderer's signs up to ~8.1 m: anchor above
    const runs = len < 10
      ? [[-1.2, 8.6, 11.2, 1.0, true], [1.4, 8.7, 12.2, 0.7, false]] as const
      : [[-3.2, 8.3, 11.4, 0.9, true], [3.4, 7.6, 12.1, 0.6, false], [0.6, 8.6, 10.6, 1.2, false]] as const;
    for (const [dt, y0, y1, sag, li] of runs) {
      const t = (f.t0 + f.t1) / 2 + dt;
      const target = f.sign * 34 - f.sign * 0.02;
      const a: [number, number, number] = f.axis === "x" ? [f.plane + f.sign * 0.02, y0, t] : [t, y0, f.plane + f.sign * 0.02];
      const b: [number, number, number] = f.axis === "x" ? [target, y1, t + dt * 0.4] : [t + dt * 0.4, y1, target];
      tryCable(a, b, sag, li, Math.round(dt * 10));
    }
  }

  // ---- 7. SKYLINE beyond the wall ---------------------------------------
  // Out-of-bounds towers so the city continues past the 14 m wall.
  const bmin = map.bounds?.min ?? [-36, 0, -36], bmax = map.bounds?.max ?? [36, 0, 36];
  const edge = Math.max(Math.abs(bmin[0]), Math.abs(bmax[0]), Math.abs(bmin[2]), Math.abs(bmax[2])) + SKYLINE_MARGIN;
  const nSky = lowSpec ? 7 : 12;
  for (let side = 0; side < 4; side++) {
    for (let k = 0; k < nSky; k++) {
      for (let row = 0; row < (lowSpec ? 1 : 2); row++) {
        const r = (s: number): number => h01(side * 50 + k, row, s);
        const w = 7 + r(70) * 9, d = 7 + r(71) * 8;
        const hgt = (row === 0 ? 17 : 26) + r(72) * (row === 0 ? 16 : 30);
        const t = -60 + (k + 0.3 + r(73) * 0.4) * (120 / nSky);
        const n = edge + 4 + d / 2 + row * 20 + r(74) * 6;
        const [x, z] = side === 0 ? [t, -n] : side === 1 ? [n, t] : side === 2 ? [-t, n] : [-n, -t];
        const sxz = side % 2 === 0 ? [w, d] : [d, w];
        push({ mat: "sky", zone: "skyline", shape: "box", host: -1, color: PALETTE.sky[Math.floor(r(75) * PALETTE.sky.length)],
          x, y: hgt / 2 - 1, z, sx: sxz[0], sy: hgt, sz: sxz[1] });
        // red aviation beacon + a neon crown on some
        push({ mat: "neon", zone: "skyline", shape: "box", host: -1, color: 0xff2020, x, y: hgt - 0.6, z, sx: 0.4, sy: 0.4, sz: 0.4 });
        if (r(76) < 0.55) {
          const c = NEON[Math.floor(r(77) * NEON.length)];
          push({ mat: "neon", zone: "skyline", shape: "box", host: -1, color: c, x, y: hgt - 1.2, z, sx: sxz[0] + 0.12, sy: 0.18, sz: sxz[1] + 0.12 });
        }
        // a big lit billboard on the inward face of some front-row towers
        if (row === 0 && r(78) < 0.45) {
          const bw = Math.min(w - 1, 9), bh = bw * 0.5, by = hgt * (0.55 + r(79) * 0.3);
          const inward = side === 0 ? [0, 1] : side === 1 ? [-1, 0] : side === 2 ? [0, -1] : [1, 0];
          const half = side % 2 === 0 ? d / 2 : d / 2;
          const half2 = Math.max(1, Math.round(bw / bh));
          const tw = bw / half2;
          for (let q = 0; q < half2; q++) {
            const off = -bw / 2 + tw * (q + 0.5);
            const px = x + inward[0] * (half + 0.05) + (side % 2 === 0 ? off : 0);
            const pz = z + inward[1] * (half + 0.05) + (side % 2 === 0 ? 0 : off);
            push({ mat: "ads", zone: "skyline", shape: "quad", host: -1, color: 0xffffff, cell: Math.floor(r(80 + q) * 4),
              x: px, y: by, z: pz, sx: side % 2 === 0 ? tw - 0.1 : 0, sy: bh - 0.1, sz: side % 2 === 0 ? 0 : tw - 0.1, nx: inward[0], nz: inward[1] });
          }
        }
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// three.js build
const tmpM = new THREE.Matrix4();
const tmpQ = new THREE.Quaternion();
const tmpV = new THREE.Vector3();
const tmpA = new THREE.Vector3();
const XAX = new THREE.Vector3(1, 0, 0);
const tmpC = new THREE.Color();

function setColor(geo: THREE.BufferGeometry, hex: number, mul: number): void {
  tmpC.setHex(hex);
  const n = geo.getAttribute("position").count;
  const c = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { c[i * 3] = tmpC.r * mul; c[i * 3 + 1] = tmpC.g * mul; c[i * 3 + 2] = tmpC.b * mul; }
  geo.setAttribute("color", new THREE.BufferAttribute(c, 3));
}

function quadGeo(p: ExtrasPiece, atlas: boolean): THREE.BufferGeometry {
  const w = p.nx !== 0 ? p.sz : p.sx;
  const hh = p.ny !== 0 ? p.sz : p.sy;
  const g = new THREE.PlaneGeometry(w, hh);
  if (atlas) {
    const u0 = (p.cell % 2) * 0.5, v0 = ((p.cell / 2) | 0) * 0.5, M = 0.006;
    const uv = g.getAttribute("uv");
    for (let i = 0; i < uv.count; i++) uv.setXY(i, u0 + M + uv.getX(i) * (0.5 - 2 * M), v0 + M + uv.getY(i) * (0.5 - 2 * M));
  } else if (p.mat === "puddle") {
    const uv = g.getAttribute("uv");
    const u0 = (p.cell % 2) * 0.5, v0 = ((p.cell / 2) | 0) * 0.5;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, u0 + uv.getX(i) * 0.5, v0 + uv.getY(i) * 0.5);
  }
  // plane faces +Z; rotate to the piece normal
  if (p.ny > 0) g.rotateX(-Math.PI / 2);
  else if (p.nx !== 0) g.rotateY(p.nx > 0 ? Math.PI / 2 : -Math.PI / 2);
  else if (p.nz < 0) g.rotateY(Math.PI);
  g.translate(p.x, p.y, p.z);
  return g;
}

function segGeo(p: ExtrasPiece): THREE.BufferGeometry {
  const a = tmpA.set(p.a![0], p.a![1], p.a![2]);
  const d = tmpV.set(p.b![0] - a.x, p.b![1] - a.y, p.b![2] - a.z);
  const len = d.length();
  const g = new THREE.BoxGeometry(len + 0.02, p.sy, p.sx);
  tmpQ.setFromUnitVectors(XAX, d.normalize());
  tmpM.compose(tmpA.set((p.a![0] + p.b![0]) / 2, (p.a![1] + p.b![1]) / 2, (p.a![2] + p.b![2]) / 2), tmpQ, tmpV.set(1, 1, 1));
  g.applyMatrix4(tmpM);
  return g;
}

let puddleTex: THREE.CanvasTexture | null = null;
let steamTex: THREE.CanvasTexture | null = null;
/** 2x2 atlas of soft irregular puddle masks (alpha in the red channel → used
 *  as alphaMap, which reads G; we write grey). Built once, module-cached. */
function puddleMask(): THREE.CanvasTexture {
  if (puddleTex) return puddleTex;
  const S = 256, c = document.createElement("canvas");
  c.width = c.height = S;
  const g = c.getContext("2d")!;
  g.fillStyle = "#000"; g.fillRect(0, 0, S, S);
  for (let cell = 0; cell < 4; cell++) {
    const ox = (cell % 2) * 128, oy = (1 - ((cell / 2) | 0)) * 128;
    g.save(); g.beginPath(); g.rect(ox, oy, 128, 128); g.clip();
    g.filter = "blur(5px)";
    for (let b = 0; b < 7; b++) {
      const r = (s: number): number => h01(cell, b, s);
      const cx = ox + 64 + (r(1) - 0.5) * 50, cy = oy + 64 + (r(2) - 0.5) * 50;
      const rad = 14 + r(3) * 24;
      g.fillStyle = `rgba(255,255,255,${0.55 + r(4) * 0.45})`;
      g.beginPath(); g.ellipse(cx, cy, rad * (0.8 + r(5) * 0.7), rad * (0.6 + r(6) * 0.5), r(7) * 3, 0, Math.PI * 2); g.fill();
    }
    g.restore();
  }
  puddleTex = new THREE.CanvasTexture(c);
  puddleTex.userData.shared = true;
  return puddleTex;
}
function steamPuff(): THREE.CanvasTexture {
  if (steamTex) return steamTex;
  const S = 128, c = document.createElement("canvas");
  c.width = c.height = S;
  const g = c.getContext("2d")!;
  g.clearRect(0, 0, S, S);
  g.filter = "blur(6px)";
  for (let b = 0; b < 16; b++) {
    const r = (s: number): number => h01(b, 9, s);
    const v = b / 15; // 0 = bottom
    const cx = 64 + (r(1) - 0.5) * (14 + v * 50), cy = S - 10 - v * (S - 30);
    const rad = 6 + v * 26 * (0.6 + r(2) * 0.6);
    const grd = g.createRadialGradient(cx, cy, 0, cx, cy, rad);
    grd.addColorStop(0, `rgba(255,255,255,${(0.55 - v * 0.4).toFixed(3)})`);
    grd.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = grd; g.beginPath(); g.arc(cx, cy, rad, 0, Math.PI * 2); g.fill();
  }
  steamTex = new THREE.CanvasTexture(c);
  steamTex.colorSpace = THREE.SRGBColorSpace;
  steamTex.userData.shared = true;
  return steamTex;
}

export function build(ctx: MapExtrasContext): THREE.Object3D | null {
  const pieces = neonDistrictLayout(ctx.map, ctx.lowSpec);
  const groups = new Map<ExtrasMat, THREE.BufferGeometry[]>();
  for (const p of pieces) {
    let g: THREE.BufferGeometry;
    if (p.shape === "quad") g = quadGeo(p, p.mat === "ads" || p.mat === "win");
    else if (p.shape === "seg") g = segGeo(p);
    else {
      g = new THREE.BoxGeometry(Math.max(p.sx, 0.005), Math.max(p.sy, 0.005), Math.max(p.sz, 0.005));
      if (p.mat === "metal" || p.mat === "sky") worldProjectUV(g, p.mat === "sky" ? 4.5 : 1.6);
      g.translate(p.x, p.y, p.z);
    }
    if (p.mat === "metal" || p.mat === "sky") setColor(g, p.color, 1);
    else if (p.mat === "neon") setColor(g, p.color, 2.2); // HDR → bloom
    (groups.get(p.mat) ?? groups.set(p.mat, []).get(p.mat)!).push(g);
  }
  const tex = mapTextures();
  const mats: Record<ExtrasMat, () => THREE.Material> = {
    metal: () => new THREE.MeshStandardMaterial(surfaceMaps("galv", { color: 0xffffff, vertexColors: true, roughness: 0.62, metalness: 0.45 }, 0.6)),
    neon: () => new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false }),
    ads: () => new THREE.MeshStandardMaterial(surfaceMaps("signage", {
      color: 0xffffff, roughness: 0.5, metalness: 0.15, emissive: 0xffffff, emissiveMap: tex.signageGlow, emissiveIntensity: 2.4,
    })),
    win: () => new THREE.MeshStandardMaterial(surfaceMaps("shopwin", {
      color: 0xffffff, roughness: 0.08, metalness: 0.55, emissive: 0xffffff, emissiveMap: tex.shopwinGlow, emissiveIntensity: 1.5,
    })),
    puddle: () => new THREE.MeshStandardMaterial({
      color: 0x3a4152, roughness: 0.03, metalness: 0.9, alphaMap: puddleMask(), transparent: true, depthWrite: false,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2, envMapIntensity: 1.6,
    }),
    steam: () => new THREE.MeshBasicMaterial({
      map: steamPuff(), color: 0x8fa2c0, transparent: true, opacity: 0.2, depthWrite: false, side: THREE.DoubleSide, fog: true,
    }),
    sky: () => new THREE.MeshStandardMaterial(surfaceMaps("facade", {
      color: 0xffffff, vertexColors: true, roughness: 0.4, metalness: 0.5, emissive: 0xffffff, emissiveMap: tex.facadeGlow, emissiveIntensity: 0.32,
    })),
  };
  const root = new THREE.Group();
  root.name = "extras:neon_district";
  for (const [mat, geos] of groups) {
    const merged = mergeGeometries(geos);
    for (const g of geos) g.dispose();
    if (!merged) { console.error(`[neon_district extras] merge failed for ${mat}`); continue; }
    merged.computeBoundingSphere();
    const mesh = new THREE.Mesh(merged, mats[mat]());
    mesh.name = `nd:${mat}`;
    mesh.castShadow = ctx.shadows && mat === "metal";
    mesh.receiveShadow = mat === "metal" || mat === "sky";
    if (mat === "puddle") mesh.renderOrder = 1;
    if (mat === "steam") mesh.renderOrder = 3;
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    root.add(mesh);
  }
  return root;
}
